import { c as _c } from "react/compiler-runtime";
import { type RechartsLabelProps as LabelProps, RechartsXAxis as XAxis, type RechartsXAxisProps as XAxisProps, RechartsYAxis as YAxis, type RechartsYAxisProps as YAxisProps } from '@precis-digital/kurama/recharts';
import { type ReactNode } from 'react';
import type { DimensionOrMetricDomain, ElementDescription, ElementDescriptionMetric, XYAxisProps } from '../internalTypes';
import type { AxisCustomDrawingProps, ChartCardTFunction, ChartType, LayoutType } from '../types';
import { chartHasAxis } from '../constants';
import { getFirstAxisLabel } from '../utils/axis';
import { createCustomXAxisTick } from './CustomXAxisTick';
import { formatDateValue, formatMetricValue, getLabelOrValueAsLabel, isNullDimensionValue } from '../utils/generics';
import { useChartCardDefaults } from '../hooks/useChartCardDefaults';
interface FirstAxisProps<DataKey extends string> {
  id: DataKey;
  chartType: ChartType;
  layout: LayoutType;
  axisDescription: ElementDescription;
  firstAxisDomain: DimensionOrMetricDomain;
  firstAxisAngledLabels: boolean;
  axisDrawing: AxisCustomDrawingProps['firstAxis'] | undefined;
  dataKey: DataKey;
  label: string;
  axisSpace: number;
  visibleLabels?: boolean[];
  t: ChartCardTFunction;
}
const FirstAxis = t0 => {
  const $ = _c(67);
  const {
    id,
    chartType,
    layout,
    axisDescription,
    firstAxisDomain,
    firstAxisAngledLabels,
    axisDrawing,
    dataKey,
    label,
    axisSpace,
    visibleLabels,
    t
  } = t0;
  const defaults = useChartCardDefaults();
  if (!chartHasAxis[chartType]) {
    return null;
  }
  let t1;
  if ($[0] !== axisDescription || $[1] !== chartType || $[2] !== dataKey || $[3] !== defaults.axis.format || $[4] !== visibleLabels) {
    t1 = (value, index) => {
      if (visibleLabels != null && !visibleLabels[index]) {
        return "";
      }
      if (isNullDimensionValue(value) && chartType !== "scatter") {
        return getLabelOrValueAsLabel(undefined, value);
      }
      if (axisDescription.type === "date") {
        return formatDateValue(dataKey, axisDescription, value);
      }
      if (chartType === "scatter") {
        return formatMetricValue(dataKey, axisDescription as ElementDescriptionMetric, value, defaults.axis.format);
      }
      return getFirstAxisLabel(axisDescription, value);
    };
    $[0] = axisDescription;
    $[1] = chartType;
    $[2] = dataKey;
    $[3] = defaults.axis.format;
    $[4] = visibleLabels;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const tickFormatter = t1;
  const t2 = id as string;
  let t3;
  if ($[6] !== defaults.fonts.axisTicks) {
    t3 = {
      ...defaults.fonts.axisTicks
    };
    $[6] = defaults.fonts.axisTicks;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== defaults.axis.space.xDY || $[9] !== defaults.axis.xLine || $[10] !== t2 || $[11] !== t3) {
    t4 = {
      xAxisId: t2,
      axisLine: defaults.axis.xLine,
      dy: defaults.axis.space.xDY,
      tick: t3
    };
    $[8] = defaults.axis.space.xDY;
    $[9] = defaults.axis.xLine;
    $[10] = t2;
    $[11] = t3;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let xAxisProps = t4;
  const t5 = id as string;
  const t6 = chartType === "scatter" || layout === "vertical" ? "left" : "right";
  let t7;
  if ($[13] !== defaults.fonts.axisTicks) {
    t7 = {
      ...defaults.fonts.axisTicks
    };
    $[13] = defaults.fonts.axisTicks;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== defaults.axis.yLine || $[16] !== t5 || $[17] !== t6 || $[18] !== t7) {
    t8 = {
      yAxisId: t5,
      orientation: t6,
      axisLine: defaults.axis.yLine,
      tick: t7
    };
    $[15] = defaults.axis.yLine;
    $[16] = t5;
    $[17] = t6;
    $[18] = t7;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let yAxisProps = t8;
  const t9 = dataKey as string;
  const t10 = firstAxisDomain != null;
  let t11;
  if ($[20] !== axisDrawing || $[21] !== firstAxisDomain || $[22] !== t10 || $[23] !== t9) {
    t11 = {
      dataKey: t9,
      tickLine: false,
      domain: firstAxisDomain,
      allowDataOverflow: t10,
      ...axisDrawing
    };
    $[20] = axisDrawing;
    $[21] = firstAxisDomain;
    $[22] = t10;
    $[23] = t9;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let axisProps = t11;
  if (firstAxisAngledLabels) {
    const t12 = xAxisProps;
    const t13 = axisSpace + defaults.axis.space.xDY;
    let t14;
    if ($[25] !== axisDescription || $[26] !== defaults || $[27] !== t || $[28] !== visibleLabels) {
      t14 = createCustomXAxisTick(axisDescription, defaults, t, visibleLabels);
      $[25] = axisDescription;
      $[26] = defaults;
      $[27] = t;
      $[28] = visibleLabels;
      $[29] = t14;
    } else {
      t14 = $[29];
    }
    const t15 = defaults.axis.angledLabels.angle < 0 ? "end" : "start";
    let t16;
    if ($[30] !== t13 || $[31] !== t14 || $[32] !== t15 || $[33] !== xAxisProps) {
      t16 = {
        ...t12,
        height: t13,
        tick: t14,
        textAnchor: t15
      };
      $[30] = t13;
      $[31] = t14;
      $[32] = t15;
      $[33] = xAxisProps;
      $[34] = t16;
    } else {
      t16 = $[34];
    }
    xAxisProps = t16;
    let t17;
    if ($[35] !== defaults.axis.space.yVertical || $[36] !== yAxisProps) {
      t17 = {
        ...yAxisProps,
        width: defaults.axis.space.yVertical,
        minTickGap: 8
      };
      $[35] = defaults.axis.space.yVertical;
      $[36] = yAxisProps;
      $[37] = t17;
    } else {
      t17 = $[37];
    }
    yAxisProps = t17;
    const t18 = axisProps.interval ?? 0;
    let t19;
    if ($[38] !== axisProps || $[39] !== t18) {
      t19 = {
        ...axisProps,
        interval: t18
      };
      $[38] = axisProps;
      $[39] = t18;
      $[40] = t19;
    } else {
      t19 = $[40];
    }
    axisProps = t19;
  } else {
    const t12 = axisSpace + defaults.axis.space.xDY;
    let t13;
    if ($[41] !== defaults.fonts.axisTicks || $[42] !== t12 || $[43] !== xAxisProps) {
      t13 = {
        scale: "auto",
        ...xAxisProps,
        height: t12,
        textAnchor: "middle",
        ...defaults.fonts.axisTicks,
        minTickGap: 16,
        interval: "preserveStartEnd"
      };
      $[41] = defaults.fonts.axisTicks;
      $[42] = t12;
      $[43] = xAxisProps;
      $[44] = t13;
    } else {
      t13 = $[44];
    }
    xAxisProps = t13;
    let t14;
    if ($[45] !== yAxisProps) {
      t14 = {
        ...yAxisProps,
        minTickGap: 8
      };
      $[45] = yAxisProps;
      $[46] = t14;
    } else {
      t14 = $[46];
    }
    yAxisProps = t14;
    let t15;
    if ($[47] !== axisProps || $[48] !== tickFormatter) {
      t15 = {
        ...axisProps,
        tickFormatter
      };
      $[47] = axisProps;
      $[48] = tickFormatter;
      $[49] = t15;
    } else {
      t15 = $[49];
    }
    axisProps = t15;
  }
  if (chartType === "scatter") {
    let t12;
    if ($[50] !== defaults.axis.space.horizontalLabelOffset || $[51] !== defaults.fonts.axisLabels || $[52] !== label) {
      t12 = {
        position: "insideBottom",
        value: label,
        ...defaults.fonts.axisLabels,
        dy: defaults.axis.space.horizontalLabelOffset
      };
      $[50] = defaults.axis.space.horizontalLabelOffset;
      $[51] = defaults.fonts.axisLabels;
      $[52] = label;
      $[53] = t12;
    } else {
      t12 = $[53];
    }
    const labelProps = t12;
    let t13;
    if ($[54] !== axisProps || $[55] !== dataKey || $[56] !== labelProps || $[57] !== layout || $[58] !== xAxisProps || $[59] !== yAxisProps) {
      t13 = layout === "vertical" ? <YAxis key={dataKey} type="number" {...yAxisProps} {...axisProps} label={labelProps} /> : <XAxis key={dataKey} type="number" {...xAxisProps} {...axisProps} label={labelProps} />;
      $[54] = axisProps;
      $[55] = dataKey;
      $[56] = labelProps;
      $[57] = layout;
      $[58] = xAxisProps;
      $[59] = yAxisProps;
      $[60] = t13;
    } else {
      t13 = $[60];
    }
    return t13;
  }
  let t12;
  if ($[61] !== axisProps || $[62] !== dataKey || $[63] !== layout || $[64] !== xAxisProps || $[65] !== yAxisProps) {
    t12 = layout === "vertical" ? <YAxis key={dataKey} type="category" {...yAxisProps} {...axisProps} /> : <XAxis key={dataKey} type="category" {...xAxisProps} {...axisProps} />;
    $[61] = axisProps;
    $[62] = dataKey;
    $[63] = layout;
    $[64] = xAxisProps;
    $[65] = yAxisProps;
    $[66] = t12;
  } else {
    t12 = $[66];
  }
  return t12;
};
export const FirstAxisNaked = t0 => {
  const $ = _c(14);
  const {
    id,
    chartType,
    layout,
    dataKey,
    firstAxisDomain
  } = t0;
  const t1 = id as string;
  let t2;
  if ($[0] !== t1) {
    t2 = {
      xAxisId: t1
    };
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const xAxisProps = t2;
  const t3 = id as string;
  let t4;
  if ($[2] !== t3) {
    t4 = {
      yAxisId: t3
    };
    $[2] = t3;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const yAxisProps = t4;
  const t5 = dataKey as string;
  const t6 = chartType === "scatter" ? "number" : "category";
  let t7;
  if ($[4] !== firstAxisDomain || $[5] !== t5 || $[6] !== t6) {
    t7 = {
      scale: "auto",
      dataKey: t5,
      domain: firstAxisDomain,
      hide: true,
      type: t6
    };
    $[4] = firstAxisDomain;
    $[5] = t5;
    $[6] = t6;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  const axisProps = t7;
  let t8;
  if ($[8] !== axisProps || $[9] !== dataKey || $[10] !== layout || $[11] !== xAxisProps || $[12] !== yAxisProps) {
    t8 = layout === "vertical" ? <YAxis key={dataKey} {...yAxisProps} {...axisProps} /> : <XAxis key={dataKey} {...xAxisProps} {...axisProps} />;
    $[8] = axisProps;
    $[9] = dataKey;
    $[10] = layout;
    $[11] = xAxisProps;
    $[12] = yAxisProps;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  return t8;
};
export default FirstAxis;